import React, { useEffect } from 'react';
import { RootState } from '../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ScrollToTop from './ScrollToTop';
import { logoutUser } from '../store/userSlice/userSlice';
import { useQueryClient } from 'react-query';
import jwtDecode from 'jwt-decode';
import { User } from '../interfaces/User';
import NoAccessPage from './NoAccessPage';
import { Drawer, Spin } from 'antd';
import { isTokenExpired, setToken } from '../pages/auth';
import { refreshToken } from '../api/base/base';
import Sidebar, { SidebarItem } from '../components/sidebar/Sidebar';
import { useUserProfile } from '../customhooks/useUserProfile';
import { hasPermission } from '../components/dashboard/dashboardpage/utils';
import { SettingOutlined } from '@ant-design/icons';
import { FrontEdgeLogo } from '../components/icons/FrontEdgeLogo';
import { HomeIcon } from '../components/icons/HomeIcon';
import { SalesIcon } from '../components/icons/SalesIcon';
import { InsuranceIcon } from '../components/icons/InsuranceIcon';
import PaymentIcon from '../components/icons/PaymentIcon';
import { LogisticsIcon } from '../components/icons/LogisticsIcon';
import { FinancingIcon } from '../components/icons/FinancingIcon';
import { TradeDocumentsIcon } from '../components/icons/TradeDocumentsIcon';
import { MenuSettingsIcon } from '../components/icons/MenuSettingsIcon';
import { CorporateIcon } from '../components/icons/CorporateIcon';
import { BookingsIcon } from '../components/icons/BookingsIcon';
import { MenuIcon } from '../components/icons/MenuIcon';
import Notifications from '../components/Notifications';
import ProtectedDashboardPage from '../components/dashboard/dashboardpage/ProtectedDashboardPage';
import { AppState, setShowDrawer } from '../store/appSlice/appSlice';
import { InvestmentIcon, TreasuryIcon } from '../components/icons';

const DashboardLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const {
        isLoading,
        permissions,
        isFetching,
        userDetails: globalUserDetails
    } = useUserProfile();

    const { showDrawer } = useSelector((state: { app: AppState }) => state.app);

    const toggleDrawer = () => {
        dispatch(setShowDrawer(!showDrawer));
    };

    const sideBarItems =
        globalUserDetails?.corporate?.status !== 1
            ? [
                  {
                      label: 'Home',
                      icon: <HomeIcon />,
                      onClick: () => {
                          navigate('/dashboard');
                      },
                      pathName: '/dashboard',
                      feature: 'Accessible',
                      level: [1, 5],
                      permissionTitle: 'home'
                  }
              ]
            : [
                  {
                      label: 'Home',
                      icon: <HomeIcon />,
                      onClick: () => {
                          navigate('/dashboard');
                      },
                      pathName: '/dashboard',
                      feature: 'Accessible',
                      level: [1, 5],
                      permissionTitle: 'home'
                  },
                  {
                      label: 'Payment',
                      icon: <PaymentIcon />,
                      pathName: '/payment',
                      submenu: [
                          {
                              label: 'Transactions',
                              onClick: () => {
                                  navigate('/payments/transactions');
                              },
                              pathName: '/payments/transactions',
                              level: [1, 5],
                              feature: 'Payments',
                              permissionTitle: 'payments'
                          },
                          {
                              label: 'Logistics Payments',
                              onClick: () => {
                                  navigate('/payments/logistics');
                              },
                              pathName: '/payments/logistics',
                              level: [1],
                              feature: 'LogisticsPayments',
                              permissionTitle: 'payments'
                          },
                          {
                              label: 'Currencies',
                              onClick: () => {
                                  navigate('/payments/currency-administration');
                              },
                              pathName: '/payments/currency-administration',
                              level: [5],
                              feature: 'Accessible',
                              permissionTitle: 'payments'
                          },
                          {
                              label: 'Dedicated Accounts',
                              onClick: () => {
                                  navigate('/payments/dedicated-accounts');
                              },
                              pathName: '/payments/dedicated-accounts',
                              level: [5],
                              feature: 'Accessible',
                              permissionTitle: 'payments'
                          }
                      ],
                      level: [1, 5],
                      feature: 'Payments',
                      permissionTitle: 'payments'
                  },

                  {
                      label: 'Financing',
                      icon: <FinancingIcon />,
                      onClick: () => {
                          navigate('/financing');
                      },
                      pathName: '/financing',
                      level: [1],
                      feature: 'Financing',
                      permissionTitle: 'financing'
                  },
                  {
                      label: 'Treasury',
                      icon: <TreasuryIcon />,
                      pathName: '/treasury',
                      feature: 'Accessible',
                      level: [1, 5],
                      permissionTitle: 'treasuryManagement',
                      submenu: [
                          {
                              label: 'Overview',
                              onClick: () => {
                                  navigate('/treasury/overview');
                              },
                              pathName: '/treasury/overview',
                              level: [1],
                              feature: 'Accessible',
                              permissionTitle: 'payments'
                          },
                          {
                              label: 'Accounts',
                              onClick: () => {
                                  navigate('/treasury/accounts');
                              },
                              pathName: '/treasury/accounts',
                              level: [1],
                              feature: 'Accessible',
                              permissionTitle: 'payments'
                          },
                          {
                              label: 'Transactions',
                              onClick: () => {
                                  navigate('/treasury/transactions');
                              },
                              pathName: '/treasury/transactions',
                              level: [1],
                              feature: 'Accessible',
                              permissionTitle: 'payments'
                          }
                      ]
                  },
                  {
                      label: 'Investment',
                      icon: <InvestmentIcon />,
                      pathName: '/investment',
                      permissionTitle: 'treasuryManagement',
                      onClick: () => {
                          navigate('/investment');
                      },
                      level: [1, 5],
                      feature: 'Investments'
                  },
                  {
                      label: 'Investment Onboarding',
                      visible: false,
                      icon: <InvestmentIcon />,
                      pathName: '/onboarding/investment',
                      permissionTitle: 'treasuryManagement',
                      onClick: () => {
                          navigate('/onboarding/investment');
                      },
                      level: [1, 5],
                      feature: 'Investments',
                      hideLayout: true
                  },
                  {
                      label: 'Sales',
                      icon: <SalesIcon />,
                      pathName: '/sales',
                      feature: 'Sales',
                      level: [1],
                      permissionTitle: 'sales',
                      submenu: [
                          {
                              label: 'Contracts',
                              onClick: () => {
                                  navigate('/sales/contracts');
                              },
                              pathName: '/sales/contracts',
                              level: [1],
                              feature: 'Sales',
                              permissionTitle: 'sales'
                          },
                          {
                              label: 'Invoices',
                              onClick: () => {
                                  navigate('/sales/invoices');
                              },
                              pathName: '/sales/invoices',
                              level: [1],
                              feature: 'Invoicing',
                              permissionTitle: 'sales'
                          }
                      ]
                  },

                  {
                      label: 'Stock Management',
                      icon: <InsuranceIcon />,
                      onClick: () => {
                          navigate('/stock');
                      },
                      pathName: '/stock',
                      feature: 'Stocks',
                      permissionTitle: 'stocks',
                      level: [1]
                  },

                  {
                      label: 'Logistics',
                      icon: <LogisticsIcon />,
                      pathName: '/bookings',
                      level: [1],
                      feature: 'BookingInttra',
                      permissionTitle: 'logistics',
                      submenu: [
                          {
                              label: 'Bookings',
                              onClick: () => {
                                  navigate('/bookings');
                              },
                              pathName: '/bookings',
                              level: [1],
                              feature: 'BookingInttra',
                              permissionTitle: 'logistics'
                          },
                          {
                              label: 'Tracking',
                              onClick: () => {
                                  navigate('/bookings/track-shipments');
                              },
                              pathName: '/bookings/track-shipments',
                              level: [1],
                              feature: 'BookingInttra',
                              permissionTitle: 'logistics'
                          },
                          {
                              label: 'Ocean Schedules',
                              onClick: () => {
                                  navigate('/bookings/ocean-schedules');
                              },
                              pathName: '/bookings/ocean-schedules',
                              level: [1],
                              feature: 'OceanSchedules',
                              permissionTitle: 'logistics'
                          }
                      ]
                  },

                  {
                      label: 'Insurance',
                      icon: <InsuranceIcon />,
                      onClick: () => {
                          navigate('/insurance');
                      },
                      pathName: '/insurance',
                      level: [1],
                      feature: 'Insurance',
                      permissionTitle: 'insurance'
                  },
                  {
                      label: 'Trade Documents',
                      icon: <TradeDocumentsIcon />,
                      onClick: () => {
                          navigate('/trade-documents');
                      },
                      pathName: '/trade-documents',
                      level: [1],
                      feature: 'TradeDocuments',
                      permissionTitle: 'tradeDocuments'
                  },
                  {
                      label: 'Settings',
                      icon: <MenuSettingsIcon />,
                      pathName: '/settings',
                      level: [1],
                      feature: 'Accessible',
                      permissionTitle: 'home',
                      submenu: [
                          {
                              label: 'Profile',
                              onClick: () => {
                                  navigate('/settings/profile');
                              },
                              pathName: '/settings/profile',
                              level: [1],
                              feature: 'Accessible',
                              permissionTitle: 'home'
                          },
                          {
                              label: 'User Management',
                              onClick: () => {
                                  navigate('/settings/user-management');
                              },
                              pathName: '/settings/user-management',
                              level: [1],
                              feature: 'Accessible',
                              permissionTitle: 'userManagement'
                          }
                      ]
                  },
                  {
                      label: 'Corporates',
                      icon: <CorporateIcon />,
                      onClick: () => {
                          navigate('/corporates');
                      },
                      pathName: '/corporates',
                      feature: 'Accessible',
                      level: [5]
                  },
                  {
                      label: 'Bookings',
                      icon: <BookingsIcon />,
                      onClick: () => {
                          navigate('/bookings/overview');
                      },
                      pathName: '/bookings/overview',
                      feature: 'Accessible',
                      level: [5]
                  }
              ];
    const handleLogout = () => {
        queryClient.removeQueries();
        navigate('/login');
        dispatch(logoutUser());
    };

    const { user, token } = useSelector((state: RootState) => ({
        user: state.user.user,
        token: state.user.token
    }));

    const userDetails = {
        name: `${user?.name}`,
        onClick: handleLogout
    };

    useEffect(() => {
        const refresh = async (): Promise<void> => {
            try {
                const res = await refreshToken();
                const { access_token, refresh_token } = res.data.data;
                const user: User = jwtDecode(access_token);
                setToken(access_token);
                localStorage.setItem('refreshToken', refresh_token);
                localStorage.setItem('exp', user.exp.toString());
            } catch (error) {
                handleLogout();
            }
        };

        if (token) {
            const interval = setInterval(() => {
                if (!isTokenExpired()) {
                    refresh();
                } else {
                    clearInterval(interval);
                    handleLogout();
                }
            }, 21540000);
        } else {
            handleLogout();
        }
        // eslint-disable-next-line
    }, [token]);

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'token' && event.newValue === null) {
                handleLogout();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
        // eslint-disable-next-line
    }, []);

    const findRoute = (
        menu: SidebarItem[],
        targetRoute: string
    ): SidebarItem | undefined => {
        const targetSegments = targetRoute.split('/').slice(0, 2).join('/');

        for (const item of menu) {
            if (item.pathName && item.pathName.startsWith(targetSegments)) {
                return item;
            }
            if (item.submenu) {
                const foundInChildren = findRoute(item.submenu, targetRoute);
                if (foundInChildren) {
                    return foundInChildren;
                }
            }
        }
        return undefined;
    };

    return (
        <>
            <Spin
                fullscreen
                spinning={isLoading || isFetching || !permissions}
                delay={500}
                indicator={
                    <SettingOutlined
                        style={{ fontSize: 100, color: '#329FC1' }}
                        spin
                    />
                }
            />

            {permissions && (
                <>
                    <main className='lg:flex bg-athensGray hidden'>
                        {findRoute(sideBarItems, location.pathname)
                            ?.hideLayout !== true && (
                            <div className='hidden lg:block'>
                                <Sidebar
                                    sideBarItems={sideBarItems}
                                    user={userDetails}
                                    activePath={location.pathname}
                                    permissions={permissions}
                                />
                            </div>
                        )}

                        <div className='lg:hidden'>
                            <div
                                onClick={toggleDrawer}
                                className='absolute z-50 lg:top-10 top-6 lg:right-24 right-16 cursor-pointer'
                            >
                                <MenuIcon />
                            </div>
                            <Drawer
                                placement='left'
                                closable={false}
                                onClose={toggleDrawer}
                                open={showDrawer}
                                width={300}
                                style={{ padding: 0, margin: 0 }}
                            >
                                <Sidebar
                                    sideBarItems={sideBarItems}
                                    user={userDetails}
                                    activePath={location.pathname}
                                    permissions={permissions}
                                />
                            </Drawer>
                        </div>
                        {hasPermission({
                            userPermissions: permissions,
                            module:
                                findRoute(sideBarItems, location.pathname)
                                    ?.permissionTitle ?? '',
                            permissionList: [
                                'view',
                                'initiate',
                                'link',
                                'connect',
                                'approve'
                            ],
                            feature: findRoute(sideBarItems, location.pathname)
                                ?.feature
                        }) && (
                            <div className='w-full h-[100vh] overflow-y-scroll relative overflow-x-hidden lg:py-8 lg:px-2 '>
                                <div className='p-4 pl-4 pt-6 lg:hidden w-max'>
                                    <FrontEdgeLogo />
                                </div>
                                <Notifications />
                                <ProtectedDashboardPage />
                                <ScrollToTop />
                            </div>
                        )}
                    </main>

                    <main className='lg:hidden bg-appcolorgrey'>
                        <div className='bg-white h-[63px] flex justify-between items-center fixed top-0 w-full pl-4 pr-2 [box-shadow:0px_1px_2px_0px_#1018280F,0px_1px_3px_0px_#1018281A] z-[9999]'>
                            <FrontEdgeLogo />

                            <div className='flex gap-4 p-2'>
                                <Notifications />
                                <button onClick={toggleDrawer}>
                                    <MenuIcon />
                                </button>

                                <Drawer
                                    placement='left'
                                    closable={false}
                                    onClose={toggleDrawer}
                                    open={showDrawer}
                                    width={300}
                                    style={{ padding: 0, margin: 0 }}
                                >
                                    <Sidebar
                                        sideBarItems={sideBarItems}
                                        user={userDetails}
                                        activePath={location.pathname}
                                        permissions={permissions}
                                    />
                                </Drawer>
                            </div>
                        </div>
                        <div className='pt-[64px]'>
                            <ProtectedDashboardPage />
                            <ScrollToTop />
                        </div>
                    </main>
                </>
            )}
            {!permissions && !isFetching && !isLoading && (
                <NoAccessPage {...{ handleLogout }} />
            )}
        </>
    );
};

export default DashboardLayout;
